import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { IdeaScoreSummaryDto, IdeasApiService, UpsertIdeaScoreRequest } from '../../shared/api/ideas-api.service';
import { IdeaDeliverableAttachmentDto, IdeaDto, IdeaListItemDto } from '../../shared/api/ideas-api.types';
import { AssignableUserDto, UsersApiService } from '../../shared/api/users-api.service';
import { AuthService } from '../../shared/auth/auth.service';
import { LoaderComponent } from '../../shared/ui/loader/loader.component';

export type KanbanColumnId =
  | 'submitted'
  | 'triage'
  | 'underReview'
  | 'needMoreInfo'
  | 'accepted'
  | 'delivered'
  | 'rejectedArchived';

/** Must match API deliverable upload limit (Program.cs). */
const maxDeliverableFileBytes = 50 * 1024 * 1024;

@Component({
  selector: 'app-committee-review-board',
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent],
  templateUrl: './committee-review-board.component.html',
  styleUrl: './committee-review-board.component.css'
})
export class CommitteeReviewBoardComponent implements OnInit, OnDestroy {
  readonly columns: {
    id: KanbanColumnId;
    label: string;
    /** API status filter; omit when the backend has no matching status yet */
    apiStatus?: string;
    droppable?: boolean;
  }[] = [
    { id: 'submitted', label: 'Submitted', apiStatus: 'Submitted' },
    { id: 'triage', label: 'Triage', apiStatus: 'Triage' },
    { id: 'underReview', label: 'Under Review', apiStatus: 'UnderReview' },
    { id: 'needMoreInfo', label: 'Need More Info', apiStatus: 'NeedMoreInfo' },
    { id: 'accepted', label: 'Accepted', apiStatus: 'Accepted' },
    { id: 'rejectedArchived', label: 'Rejected/Archived' },
    { id: 'delivered', label: 'Delivered', apiStatus: 'Delivered', droppable: false }
  ];

  columnIdeas: Record<KanbanColumnId, IdeaListItemDto[]> = {
    submitted: [],
    triage: [],
    underReview: [],
    needMoreInfo: [],
    accepted: [],
    delivered: [],
    rejectedArchived: []
  };

  loading = false;
  errorMessage = '';
  assignedToMe = false;
  dragSourceColumn: KanbanColumnId | null = null;
  dragIdeaId: string | null = null;
  dropTargetColumn: KanbanColumnId | null = null;
  draggingIdea = false;
  private deferredDecisionError: string | null = null;

  /** Committee review modal */
  reviewModalOpen = false;
  reviewLoading = false;
  reviewError = '';
  modalIdea: IdeaDto | null = null;
  committeeNotes = '';
  decisionError = '';
  savingDecision = false;
  promotingToReview = false;
  pendingDecision: 'accept' | 'reject' | 'park' | 'needMoreInfo' | null = null;

  readonly scoreRows = [
    { key: 'impact' as const, label: 'Impact' },
    { key: 'feasibility' as const, label: 'Feasibility' },
    { key: 'strategicAlignment' as const, label: 'Strategic Alignment' },
    { key: 'urgency' as const, label: 'Urgency' }
  ];

  scores = { impact: 3, feasibility: 3, strategicAlignment: 3, urgency: 3 };

  assignableUsers: AssignableUserDto[] = [];
  assignableUsersError = '';
  selectedAssigneeUserId = '';
  savingAssignee = false;
  assigneeSaveError = '';
  private assigneeSelectionArmed = false;
  deliverableUploadError = '';
  deliveryProofFiles: File[] = [];
  deliveryProofError = '';
  deliveringWithProof = false;
  /** Column of the card used to open the review modal (drives Move to Delivered visibility). */
  modalOpenedFromColumn: KanbanColumnId | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new BehaviorSubject<void>(undefined);

  constructor(
    private readonly api: IdeasApiService,
    private readonly usersApi: UsersApiService,
    private readonly auth: AuthService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.auth.refreshCurrentUser().pipe(takeUntil(this.destroy$)).subscribe();
    this.loadAssignableUsers();
    this.bindBoardQuery();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.reviewModalOpen) {
      this.closeReviewModal();
    }
  }

  toggleAssignedToMe(): void {
    this.assignedToMe = !this.assignedToMe;
    this.reload$.next();
  }

  countFor(columnId: KanbanColumnId): number {
    return this.columnIdeas[columnId]?.length ?? 0;
  }

  onCardDragStart(event: DragEvent, idea: IdeaListItemDto, fromColumn: KanbanColumnId): void {
    if (!this.isColumnDraggable(fromColumn)) {
      event.preventDefault();
      return;
    }

    this.dragSourceColumn = fromColumn;
    this.dragIdeaId = idea.id;
    this.dropTargetColumn = null;
    this.draggingIdea = true;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', idea.id);
    }
  }

  onCardDragEnd(): void {
    this.resetDragState();
  }

  onColumnDragOver(event: DragEvent, columnId: KanbanColumnId): void {
    if (!this.isColumnDroppable(columnId)) return;
    if (!this.dragIdeaId || !this.dragSourceColumn) return;
    event.preventDefault();
    this.dropTargetColumn = columnId;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.dragSourceColumn === columnId ? 'none' : 'move';
    }
  }

  onColumnDragLeave(columnId: KanbanColumnId): void {
    if (this.dropTargetColumn === columnId) {
      this.dropTargetColumn = null;
    }
  }

  onColumnDrop(event: DragEvent, toColumn: KanbanColumnId): void {
    if (!this.isColumnDroppable(toColumn)) {
      return;
    }

    event.preventDefault();
    const fromColumn = this.dragSourceColumn;
    const ideaId = this.dragIdeaId;
    this.resetDragState();

    if (!fromColumn || !ideaId || fromColumn === toColumn) {
      return;
    }

    const toStatus = this.columnToStatus(toColumn);
    if (!toStatus) {
      this.errorMessage = 'This column cannot be targeted by drag-and-drop.';
      return;
    }

    const sourceList = this.columnIdeas[fromColumn] ?? [];
    const idx = sourceList.findIndex(i => i.id === ideaId);
    if (idx < 0) {
      return;
    }

    const targetIdea = sourceList[idx];
    if (toStatus === 'Accepted' || toStatus === 'Rejected') {
      this.deferredDecisionError = 'A committee score is required before accepting or rejecting an idea.';
      this.openReviewModal(targetIdea, fromColumn);
      return;
    }

    const [idea] = sourceList.splice(idx, 1);
    this.columnIdeas[toColumn] = [idea, ...(this.columnIdeas[toColumn] ?? [])];
    this.columnIdeas = { ...this.columnIdeas, [fromColumn]: sourceList };
    this.errorMessage = '';

    this.api
      .changeIdeaStatus(idea.id, { status: toStatus })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.reload$.next();
        },
        error: (err: unknown) => {
          const movedList = this.columnIdeas[toColumn] ?? [];
          const movedIdx = movedList.findIndex(i => i.id === idea.id);
          if (movedIdx >= 0) {
            movedList.splice(movedIdx, 1);
          }
          this.columnIdeas[fromColumn] = [idea, ...(this.columnIdeas[fromColumn] ?? [])];
          this.columnIdeas = { ...this.columnIdeas, [toColumn]: movedList };
          this.errorMessage = this.formatDecisionApiError(err);
        }
      });
  }

  private resetDragState(): void {
    this.dragSourceColumn = null;
    this.dragIdeaId = null;
    this.dropTargetColumn = null;
    this.draggingIdea = false;
  }

  isColumnDroppable(columnId: KanbanColumnId): boolean {
    return this.columns.find((col) => col.id === columnId)?.droppable !== false;
  }

  isColumnDraggable(columnId: KanbanColumnId): boolean {
    return columnId !== 'delivered' && this.isColumnDroppable(columnId);
  }

  onDeliveryProofSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const nextFiles = Array.from(input?.files ?? []);
    if (nextFiles.length > 0) {
      const existingKeys = new Set(this.deliveryProofFiles.map((file) => this.deliveryProofFileKey(file)));
      const uniqueNewFiles = nextFiles.filter((file) => !existingKeys.has(this.deliveryProofFileKey(file)));
      const tooLarge = uniqueNewFiles.filter((f) => f.size > maxDeliverableFileBytes);
      const ok = uniqueNewFiles.filter((f) => f.size <= maxDeliverableFileBytes);
      if (tooLarge.length > 0) {
        this.deliveryProofError = `Each file must be 50 MB or smaller (${tooLarge.map((f) => f.name).join(', ')}).`;
      } else {
        this.deliveryProofError = '';
      }
      this.deliveryProofFiles = [...this.deliveryProofFiles, ...ok];
    } else {
      this.deliveryProofError = '';
    }
    if (input) {
      input.value = '';
    }
  }

  removeDeliveryProofFile(index: number): void {
    if (this.deliveringWithProof) {
      return;
    }

    this.deliveryProofFiles = this.deliveryProofFiles.filter((_, fileIndex) => fileIndex !== index);
  }

  moveAcceptedToDelivered(fileInput: HTMLInputElement): void {
    const idea = this.modalIdea;
    const files = this.deliveryProofFiles;
    if (!idea || !this.showMoveToDeliveredSection || this.deliveringWithProof) {
      return;
    }
    if (files.length === 0) {
      this.deliveryProofError = 'Upload at least one proof file before moving this idea to Delivered.';
      return;
    }

    this.deliveringWithProof = true;
    this.deliveryProofError = '';
    let proofUploaded = false;

    this.api
      .uploadIdeaDeliverables(idea.id, files)
      .pipe(
        tap(() => {
          proofUploaded = true;
        }),
        switchMap(() => this.api.changeIdeaStatus(idea.id, { status: 'Delivered' })),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.deliveringWithProof = false;
          this.deliveryProofFiles = [];
          this.deliveryProofError = '';
          fileInput.value = '';
          this.closeReviewModal();
          this.reload$.next();
        },
        error: (err: unknown) => {
          this.deliveringWithProof = false;

          if (!proofUploaded) {
            this.deliveryProofError = this.formatApiError(err, 'Could not upload proof.');
            return;
          }

          this.deliveryProofFiles = [];
          fileInput.value = '';
          this.deliveryProofError = this.formatApiError(
            err,
            'Proof was saved, but the idea could not be moved to Delivered. Try moving it to Delivered again from the board.'
          );
          this.reload$.next();
        }
      });
  }

  openReviewModal(idea: IdeaListItemDto, fromColumn: KanbanColumnId): void {
    this.modalOpenedFromColumn = fromColumn;
    this.loadAssignableUsers();
    this.reviewModalOpen = true;
    this.reviewLoading = true;
    this.reviewError = '';
    this.decisionError = '';
    this.assigneeSaveError = '';
    this.pendingDecision = null;
    this.promotingToReview = false;
    this.modalIdea = null;
    this.committeeNotes = '';
    this.resetModalScores();
    this.assigneeSelectionArmed = false;
    this.selectedAssigneeUserId = '';
    this.deliverableUploadError = '';
    this.deliveryProofFiles = [];
    this.deliveryProofError = '';
    this.deliveringWithProof = false;

    forkJoin({
      idea: this.api.getIdeaById(idea.id),
      scoreSummary: this.api.getIdeaScore(idea.id).pipe(catchError(() => of(null)))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ idea: dto, scoreSummary }) => {
          this.modalIdea = dto;
          const fromList = idea.assignedReviewerId?.trim();
          const fromDto = dto.assignedReviewerId?.trim();
          this.selectedAssigneeUserId = (fromDto || fromList) ?? '';
          this.applyScoreSummaryToModal(scoreSummary);
          if (this.deferredDecisionError) {
            this.decisionError = this.deferredDecisionError;
            this.deferredDecisionError = null;
          }
          this.reviewLoading = false;
          queueMicrotask(() => {
            this.assigneeSelectionArmed = true;
          });
        },
        error: () => {
          this.reviewError = 'Failed to load idea.';
          this.reviewLoading = false;
        }
      });
  }

  private resetModalScores(): void {
    this.scores = { impact: 3, feasibility: 3, strategicAlignment: 3, urgency: 3 };
  }

  /** Loads saved committee score for current user (API 1–10 → sliders 1–5). */
  private applyScoreSummaryToModal(summary: IdeaScoreSummaryDto | null): void {
    this.resetModalScores();
    if (!summary) {
      return;
    }

    const cur = this.normalizeCurrentUserScore(summary);
    if (!cur) {
      return;
    }

    this.scores = {
      impact: this.criterionToSlider(cur.c1),
      feasibility: this.criterionToSlider(cur.c2),
      strategicAlignment: this.criterionToSlider(cur.c3),
      urgency: this.criterionToSlider(cur.c4)
    };

    const notes = cur.notes?.trim();
    if (notes) {
      this.committeeNotes = notes;
    }
  }

  private normalizeCurrentUserScore(summary: IdeaScoreSummaryDto): {
    c1: number;
    c2: number;
    c3: number;
    c4: number;
    notes?: string | null;
  } | null {
    const raw = summary as unknown as Record<string, unknown>;
    const node = (raw['currentUserScore'] ?? raw['CurrentUserScore']) as Record<string, unknown> | null | undefined;
    if (!node) {
      return null;
    }

    const n = (k: string): number | null => {
      const v = node[k] ?? node[k.charAt(0).toUpperCase() + k.slice(1)];
      if (typeof v === 'number' && Number.isFinite(v)) {
        return v;
      }
      return null;
    };

    const c1 = n('criterion1Score');
    const c2 = n('criterion2Score');
    const c3 = n('criterion3Score');
    const c4 = n('criterion4Score');
    if (c1 === null || c2 === null || c3 === null || c4 === null) {
      return null;
    }

    const notesVal = node['notes'] ?? node['Notes'];
    const notes = typeof notesVal === 'string' ? notesVal : null;

    return { c1, c2, c3, c4, notes };
  }

  /** Inverse of buildCommitteeScoreRequest: API stores 1–10, sliders use 1–5. */
  private criterionToSlider(criterion1To10: number): number {
    return Math.min(5, Math.max(1, Math.round(criterion1To10 / 2)));
  }

  closeReviewModal(): void {
    this.reviewModalOpen = false;
    this.modalOpenedFromColumn = null;
    this.modalIdea = null;
    this.assigneeSelectionArmed = false;
    this.assigneeSaveError = '';
    this.reviewError = '';
    this.decisionError = '';
    this.pendingDecision = null;
    this.savingDecision = false;
    this.promotingToReview = false;
    this.deliverableUploadError = '';
    this.deliveryProofFiles = [];
    this.deliveryProofError = '';
    this.deliveringWithProof = false;
  }

  viewFullDetails(): void {
    if (!this.modalIdea) return;
    const id = this.modalIdea.id;
    this.closeReviewModal();
    void this.router.navigate(['/ideas', id]);
  }

  summaryText(idea: IdeaDto): string {
    const d = idea.description?.trim();
    if (d) return d;
    const p = idea.problemOpportunity?.trim();
    if (p) return p;
    const s = idea.proposedSolution?.trim();
    if (s) return s;
    return 'No summary available.';
  }

  get committeeCanDecide(): boolean {
    return !!this.modalIdea && !this.reviewLoading && !this.isDeliveredIdea;
  }

  get committeeDecisionHint(): string {
    if (!this.modalIdea || this.reviewLoading || this.isDeliveredIdea) {
      return '';
    }

    if (!this.hasAssignedOwner) {
      return 'Assign owner before approving this idea.';
    }

    return '';
  }

  get isDeliveredIdea(): boolean {
    return !!this.modalIdea && this.ideaStatusCode(this.modalIdea) === 8;
  }

  get isAcceptedIdea(): boolean {
    return !!this.modalIdea && this.ideaStatusCode(this.modalIdea) === 4;
  }

  /** Move to Delivered only for ideas that are approved (Accepted) and opened from the Accepted column card. */
  get showMoveToDeliveredSection(): boolean {
    if (!this.modalIdea || this.reviewLoading) {
      return false;
    }
    if (this.ideaStatusCode(this.modalIdea) !== 4) {
      return false;
    }
    return this.modalOpenedFromColumn === 'accepted';
  }

  get hasAssignedOwner(): boolean {
    return this.selectedAssigneeUserId.trim().length > 0;
  }

  get canApproveCurrentIdea(): boolean {
    return this.committeeCanDecide && this.hasAssignedOwner && !this.savingAssignee;
  }

  get canMoveAcceptedToDelivered(): boolean {
    return this.showMoveToDeliveredSection && !this.deliveringWithProof && this.deliveryProofFiles.length > 0;
  }

  /** Submitted (1) or Triage (2): can promote to Under Review */
  get canSendToUnderReview(): boolean {
    if (!this.modalIdea || this.reviewLoading) return false;
    const code = this.ideaStatusCode(this.modalIdea);
    return code === 1 || code === 2;
  }

  promoteToUnderReview(): void {
    if (!this.modalIdea || this.promotingToReview || this.savingDecision || !this.canSendToUnderReview) return;

    this.promotingToReview = true;
    this.decisionError = '';
    const note = this.committeeNotes.trim() || null;

    this.api
      .changeIdeaStatus(this.modalIdea.id, { status: 'UnderReview', note })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.promotingToReview = false;
          this.closeReviewModal();
          this.reload$.next();
        },
        error: () => {
          this.promotingToReview = false;
          this.decisionError =
            'Could not move this idea to Under Review. You need the Moderator or Committee role.';
        }
      });
  }

  submitDecision(kind: 'accept' | 'reject' | 'park' | 'needMoreInfo'): void {
    if (!this.modalIdea || this.savingDecision || this.promotingToReview || !this.committeeCanDecide) return;

    if (kind === 'accept' && !this.hasAssignedOwner) {
      this.decisionError = 'Assign owner before approving this idea.';
      return;
    }

    const statusMap: Record<'accept' | 'reject' | 'park' | 'needMoreInfo', string> = {
      accept: 'Accepted',
      reject: 'Rejected',
      park: 'Archived',
      needMoreInfo: 'NeedMoreInfo'
    };

    const status = statusMap[kind];
    this.savingDecision = true;
    this.pendingDecision = kind;
    this.decisionError = '';

    const note = this.committeeNotes.trim() || null;
    const needsScoreFirst = kind === 'accept' || kind === 'reject';
    const status$ = this.api.changeIdeaStatus(this.modalIdea.id, { status, note });
    const pipeline = needsScoreFirst
      ? this.api.upsertIdeaScore(this.modalIdea.id, this.buildCommitteeScoreRequest(note)).pipe(switchMap(() => status$))
      : status$;

    pipeline.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.savingDecision = false;
        this.pendingDecision = null;
        this.closeReviewModal();
        this.reload$.next();
      },
      error: (err: unknown) => {
        this.savingDecision = false;
        this.pendingDecision = null;
        this.decisionError = this.formatDecisionApiError(err);
      }
    });
  }

  deliverableAttachments(idea: IdeaDto): IdeaDeliverableAttachmentDto[] {
    return idea.deliverableAttachments ?? [];
  }

  downloadDeliverable(ideaId: string, attachment: IdeaDeliverableAttachmentDto): void {
    this.api
      .downloadIdeaDeliverable(ideaId, attachment.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = attachment.fileName;
          link.target = '_blank';
          link.rel = 'noopener';
          link.click();
          setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        },
        error: (err: unknown) => {
          this.deliverableUploadError = this.formatApiError(err, 'Could not download the file.');
        }
      });
  }

  formatFileSize(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    const precision = size >= 10 || unitIndex === 0 ? 0 : 1;
    return `${size.toFixed(precision)} ${units[unitIndex]}`;
  }

  selectedDeliveryProofSummary(): string {
    const count = this.deliveryProofFiles.length;
    if (count === 0) {
      return '';
    }
    if (count === 1) {
      return this.deliveryProofFiles[0].name;
    }
    return `${count} files selected`;
  }

  private deliveryProofFileKey(file: File): string {
    return `${file.name}::${file.size}::${file.lastModified}`;
  }

  isImageAttachment(attachment: IdeaDeliverableAttachmentDto): boolean {
    return attachment.contentType?.toLowerCase().startsWith('image/') ?? false;
  }

  /** Maps modal sliders (1–5) to API criteria (1–10). Backend requires scores before Accept/Reject. */
  private buildCommitteeScoreRequest(note: string | null): UpsertIdeaScoreRequest {
    const to10 = (v: number) => Math.min(10, Math.max(1, Math.round(v * 2)));
    const c1 = to10(this.scores.impact);
    const c2 = to10(this.scores.feasibility);
    const c3 = to10(this.scores.strategicAlignment);
    const c4 = to10(this.scores.urgency);
    const c5 = Math.min(10, Math.max(1, Math.round((c1 + c2 + c3 + c4) / 4)));
    return {
      criterion1Score: c1,
      criterion2Score: c2,
      criterion3Score: c3,
      criterion4Score: c4,
      criterion5Score: c5,
      notes: note
    };
  }

  private formatDecisionApiError(err: unknown): string {
    return this.formatApiError(
      err,
      'This action was not allowed. You may need the Committee role, the idea may be in the wrong status, or scoring must succeed before approve/reject.'
    );
  }

  private formatApiError(err: unknown, fallback: string): string {
    if (!(err instanceof HttpErrorResponse)) {
      return fallback;
    }

    if (err.status === 0) {
      return 'Cannot reach the API (network or CORS). Confirm the server is running and the app proxy targets it.';
    }

    if (err.status === 413) {
      return 'The upload is larger than the server allows. Try smaller files or fewer at once.';
    }

    if (err.status === 401) {
      return 'Your session expired or you are not signed in. Sign in and try again.';
    }

    const body = err.error;
    if (typeof body === 'string' && body.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(body) as Record<string, unknown>;
        const fromParsed = this.firstValidationMessageFromBody(parsed);
        if (fromParsed) {
          return fromParsed;
        }
      } catch {
        /* ignore */
      }
    }

    if (body && typeof body === 'object') {
      const fromObj = this.firstValidationMessageFromBody(body as Record<string, unknown>);
      if (fromObj) {
        return fromObj;
      }
    }

    if (typeof body === 'string' && body.trim() && !body.trim().startsWith('<')) {
      const t = body.trim();
      return t.length > 400 ? `${t.slice(0, 400)}…` : t;
    }

    if (err.status === 403) {
      return 'You are not allowed to perform this action.';
    }
    if (err.status === 400) {
      return 'The server rejected the request. Please review the form values and try again.';
    }

    if (err.status >= 500) {
      const hint = err.message?.trim();
      if (hint) {
        return `Server error (${err.status}). ${hint}`;
      }
      return `Server error (${err.status}). Check API logs.`;
    }

    return fallback;
  }

  private firstValidationMessageFromBody(rec: Record<string, unknown>): string | null {
    const errors = rec['errors'] ?? rec['Errors'];
    if (errors && typeof errors === 'object') {
      for (const val of Object.values(errors as Record<string, unknown>)) {
        if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string' && val[0].trim()) {
          return val[0];
        }
      }
    }
    const detail = rec['detail'] ?? rec['Detail'];
    if (typeof detail === 'string' && detail.trim()) {
      return detail.trim();
    }
    const title = rec['title'] ?? rec['Title'];
    if (typeof title === 'string' && title.trim()) {
      return title.trim();
    }
    const message = rec['message'] ?? rec['Message'];
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
    return null;
  }

  private ideaStatusCode(idea: IdeaDto): number | null {
    const raw = this.rawIdeaStatusValue(idea);
    return this.normalizeIdeaStatus(raw);
  }

  /** API may send `status` (camelCase) or `Status` (PascalCase); value may be number or string. */
  private rawIdeaStatusValue(idea: IdeaDto): unknown {
    const extended = idea as IdeaDto & { Status?: unknown };
    const v = extended.status ?? extended.Status;
    return v;
  }

  private normalizeIdeaStatus(status: unknown): number | null {
    if (status === null || status === undefined) return null;
    if (typeof status === 'number' && Number.isFinite(status)) {
      return Math.trunc(status);
    }
    if (typeof status === 'string') {
      const trimmed = status.trim();
      if (trimmed === '') return null;
      const asNum = Number(trimmed);
      if (Number.isFinite(asNum) && String(Math.trunc(asNum)) === trimmed) {
        return Math.trunc(asNum);
      }
      const key = trimmed.replace(/[\s_-]+/g, '').toLowerCase();
      const byName: Record<string, number> = {
        submitted: 1,
        triage: 2,
        underreview: 3,
        accepted: 4,
        approved: 4,
        rejected: 5,
        planned: 6,
        inprogress: 7,
        delivered: 8,
        archived: 9,
        needmoreinfo: 10
      };
      return byName[key] ?? null;
    }
    return null;
  }

  private mergeIdeaLists(a: IdeaListItemDto[], b: IdeaListItemDto[]): IdeaListItemDto[] {
    const map = new Map<string, IdeaListItemDto>();
    for (const item of a) {
      map.set(item.id, item);
    }
    for (const item of b) {
      map.set(item.id, item);
    }
    return [...map.values()].sort((x, y) => {
      const ax = x.createdAtUtc ? Date.parse(x.createdAtUtc) : 0;
      const ay = y.createdAtUtc ? Date.parse(y.createdAtUtc) : 0;
      return ay - ax;
    });
  }

  private columnToStatus(columnId: KanbanColumnId): string | null {
    const byColumn: Record<KanbanColumnId, string | null> = {
      submitted: 'Submitted',
      triage: 'Triage',
      underReview: 'UnderReview',
      needMoreInfo: 'NeedMoreInfo',
      accepted: 'Accepted',
      rejectedArchived: 'Rejected',
      delivered: null
    };
    return byColumn[columnId];
  }

  private bindBoardQuery(): void {
    combineLatest([this.reload$])
      .pipe(
        tap(() => {
          this.loading = true;
          this.errorMessage = '';
        }),
        switchMap(() => {
          const userId = this.auth.currentUser?.userId ?? '';
          const assignedTo = this.assignedToMe && userId ? userId : '';

          const empty = of({ items: [] as IdeaListItemDto[], totalCount: 0 });
          const fetch = (status: string) =>
            this.api
              .getIdeasWithOptions({
                page: 1,
                pageSize: 100,
                status,
                assignedTo
              })
              .pipe(catchError(() => empty));

          return forkJoin({
            submitted: fetch('Submitted'),
            triage: fetch('Triage'),
            underReview: fetch('UnderReview'),
            needMoreInfo: fetch('NeedMoreInfo'),
            accepted: fetch('Accepted'),
            delivered: fetch('Delivered'),
            rejected: fetch('Rejected'),
            archived: fetch('Archived')
          }).pipe(
            map((res) => {
              const rejectedArchived = this.mergeIdeaLists(
                res.rejected.items ?? [],
                res.archived.items ?? []
              );
              return {
                submitted: res.submitted.items ?? [],
                triage: res.triage.items ?? [],
                underReview: res.underReview.items ?? [],
                needMoreInfo: res.needMoreInfo.items ?? [],
                accepted: res.accepted.items ?? [],
                delivered: res.delivered.items ?? [],
                rejectedArchived
              };
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (cols) => {
          this.columnIdeas = {
            submitted: cols.submitted,
            triage: cols.triage,
            underReview: cols.underReview,
            needMoreInfo: cols.needMoreInfo,
            accepted: cols.accepted,
            delivered: cols.delivered,
            rejectedArchived: cols.rejectedArchived
          };
          this.loading = false;
        },
        error: () => {
          this.errorMessage = 'Failed to load ideas. Please try again.';
          this.loading = false;
        }
      });
  }

  getSecondaryPillText(idea: IdeaListItemDto): string {
    if (idea.challengeTitle) {
      return idea.challengeTitle.length > 24 ? idea.challengeTitle.slice(0, 22) + '…' : idea.challengeTitle;
    }
    if (idea.departmentName) {
      return idea.departmentName.length > 20 ? idea.departmentName.slice(0, 18) + '…' : idea.departmentName;
    }
    return '';
  }

  getUpvoteCount(idea: IdeaListItemDto): number {
    return Math.max(0, idea.upvoteCount ?? idea.voteCount ?? 0);
  }

  authorLabel(idea: IdeaListItemDto): string {
    if (idea.ownerDisplayName?.trim()) {
      return idea.ownerDisplayName.trim();
    }
    return 'Unknown author';
  }

  formatDate(iso: string): string {
    if (!iso?.trim()) {
      return '—';
    }

    const value = new Date(iso);
    if (Number.isNaN(value.getTime())) {
      return '—';
    }

    return value.toLocaleString();
  }

  assigneeOptionLabel(user: AssignableUserDto): string {
    const name = user.displayName?.trim() || user.email;
    return `${name}`;
  }

  private loadAssignableUsers(): void {
    this.assignableUsersError = '';
    this.usersApi
      .getAssignableUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          this.assignableUsers = list.filter(user => user.email !== 'admin@masterteam.sa') ?? [];
        },
        error: () => {
          this.assignableUsersError = 'Could not load users for assignment.';
        }
      });
  }

  onAssigneeUserPicked(value: string): void {
    if (!this.assigneeSelectionArmed || !this.modalIdea || this.savingAssignee) {
      return;
    }

    const ideaId = this.modalIdea.id;
    const reviewerId = value === '' ? null : value;

    this.savingAssignee = true;
    this.assigneeSaveError = '';
    this.api
      .setIdeaAssignee(ideaId, reviewerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.savingAssignee = false;
          this.modalIdea = {
            ...this.modalIdea!,
            assignedReviewerId: reviewerId ?? undefined
          };
          this.reload$.next();
        },
        error: () => {
          this.savingAssignee = false;
          this.assigneeSaveError = 'Could not update assignment.';
          this.selectedAssigneeUserId = this.modalIdea?.assignedReviewerId?.trim() ?? '';
        }
      });
  }
}
